import React, { useState, useCallback, useEffect } from "react";
import Header from "../Header";
import apiClient from "../../api/client";
import { toast } from "react-toastify";
import "./index.css";

/**
 * Your provider's new RailWire bridge needs a session kept alive by a
 * human periodically solving a captcha — there's no automatic OCR retry
 * like the old flow had. This page is that manual step: check whether
 * the session is healthy, and if not, fetch a captcha image, read it,
 * and submit what it says.
 */
const RailwireSessionManager = () => {
  const [session, setSession] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  const [captchaImage, setCaptchaImage] = useState(null); // data: URL
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captchaText, setCaptchaText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const { data } = await apiClient.get("/railwire_session_status.php");
      if (data.success) {
        setSession(data.session);
        // Clear any stale captcha if the session turns out to already
        // be healthy (e.g. someone else just logged it in).
        if (data.session?.status === "HEALTHY") {
          setCaptchaImage(null);
          setCaptchaText("");
        }
      } else {
        toast.error(data.message || "Failed to check RailWire session status");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to check RailWire session status");
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const fetchCaptcha = async () => {
    setCaptchaLoading(true);
    setCaptchaText("");
    try {
      const { data } = await apiClient.get("/railwire_get_captcha.php");
      if (data.success) {
        setCaptchaImage(`data:${data.content_type};base64,${data.image_base64}`);
      } else {
        toast.error(data.message || "Failed to load captcha");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load captcha");
    } finally {
      setCaptchaLoading(false);
    }
  };

  const submitCaptcha = async () => {
    if (!captchaText.trim()) {
      toast.warn("Enter what the captcha shows first.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await apiClient.post("/railwire_submit_login.php", {
        captcha: captchaText.trim(),
      });
      if (data.success) {
        toast.success(data.message || "RailWire login successful");
        setCaptchaImage(null);
        setCaptchaText("");
        fetchStatus();
      } else {
        toast.error(data.message || "Login failed — check the captcha and try again");
        // Wrong captcha usually means a fresh one is needed too.
        fetchCaptcha();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Login failed");
      fetchCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  const isHealthy = session?.status === "HEALTHY";
  const isLoginRequired = session?.status === "LOGIN_REQUIRED";

  return (
    <>
      <Header />
      <div className="railwire-container">
        <h1>RailWire Session</h1>
        <p className="railwire-subtitle">
          Your provider's bridge needs an active login session to serve RailTel/RailWire
          requests — this keeps it alive.
        </p>

        <div className="railwire-status-card">
          {loadingStatus ? (
            <p>Checking session status…</p>
          ) : !session ? (
            <p className="railwire-error-text">Could not reach the RailWire bridge.</p>
          ) : (
            <>
              <div className="railwire-status-row">
                <span
                  className={`railwire-status-badge ${isHealthy ? "healthy" : "login-required"}`}
                >
                  {isHealthy ? "Session Healthy" : "Login Required"}
                </span>
                <button className="railwire-refresh-btn" onClick={fetchStatus} disabled={loadingStatus}>
                  Refresh
                </button>
              </div>

              {isHealthy && (
                <div className="railwire-details">
                  <div><strong>Session started:</strong> {session.session_started || "—"}</div>
                  <div><strong>Last activity:</strong> {session.last_activity || "—"}</div>
                  <div>
                    <strong>Duration:</strong>{" "}
                    {session.session_duration_seconds != null
                      ? `${Math.floor(session.session_duration_seconds / 60)} min`
                      : "—"}
                  </div>
                </div>
              )}

              {isLoginRequired && (
                <div className="railwire-details">
                  <div>
                    <strong>Logged out at:</strong> {session.logged_out_at || "—"}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {isLoginRequired && (
          <div className="railwire-login-card">
            <h2>Log RailWire back in</h2>

            {!captchaImage ? (
              <button className="railwire-btn railwire-btn--primary" onClick={fetchCaptcha} disabled={captchaLoading}>
                {captchaLoading ? "Loading…" : "Get Captcha"}
              </button>
            ) : (
              <>
                <img src={captchaImage} alt="RailWire login captcha" className="railwire-captcha-image" />

                <div className="railwire-captcha-row">
                  <input
                    type="text"
                    placeholder="Type what the captcha shows"
                    value={captchaText}
                    onChange={(e) => setCaptchaText(e.target.value)}
                    disabled={submitting}
                  />
                  <button className="railwire-btn railwire-btn--outline" onClick={fetchCaptcha} disabled={captchaLoading || submitting}>
                    New Captcha
                  </button>
                </div>

                <button
                  className="railwire-btn railwire-btn--primary"
                  onClick={submitCaptcha}
                  disabled={submitting || !captchaText.trim()}
                >
                  {submitting ? "Logging in…" : "Submit"}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default RailwireSessionManager;
